import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Rating,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Directions as DirectionsIcon,
  LocalHospital as DoctorIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@services/authService';

interface Clinic {
  id: string;
  name: string;
  address: any;
  phone?: string;
  email?: string;
  logo?: string;
  rating: number;
  reviewsCount: number;
  services: string[];
  specialties: string[];
  isFavorite?: boolean;
  description?: string;
  workingHours?: any;
  // Helper properties
  addressText?: string;
  city?: string;
  county?: string;
}

interface Veterinarian {
  id: string;
  firstName: string;
  lastName: string;
  specialization?: string;
  photo_url?: string;
}

const ClinicDetails: React.FC = () => {
  const navigate = useNavigate();
  const { clinicId } = useParams<{ clinicId: string }>();
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [veterinarians, setVeterinarians] = useState<Veterinarian[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (clinicId) {
      loadClinicDetails();
      loadVeterinarians();
    }
  }, [clinicId]);

  const loadClinicDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Pentru moment, folosim date mock - in viitor va fi o ruta specifica
      const response = await api.get('/clinics/partner');
      const clinicsData = response.data.data || [];
      
      const clinicData = clinicsData.find((c: any) => c.id === clinicId);
      if (!clinicData) {
        setError('Clinica nu a fost gasita');
        return;
      }

      // Proceseaza datele pentru a extrage informatii din JSONB
      let addressText = '';
      let city = '';
      let county = '';
      
      if (typeof clinicData.address === 'string') {
        addressText = clinicData.address;
      } else if (clinicData.address && typeof clinicData.address === 'object') {
        addressText = clinicData.address.street || clinicData.address.full || '';
        city = clinicData.address.city || clinicData.address.locality || '';
        county = clinicData.address.county || '';
      }

      const processedClinic = {
        ...clinicData,
        addressText,
        city: city || 'Necunoscut',
        county,
        description: clinicData.description || 'Clinica veterinara de incredere, dedicata sanatatii si bunastarii animalelor tale.',
        workingHours: clinicData.workingHours || {
          monday: '08:00 - 18:00',
          tuesday: '08:00 - 18:00',
          wednesday: '08:00 - 18:00',
          thursday: '08:00 - 18:00',
          friday: '08:00 - 18:00',
          saturday: '09:00 - 14:00',
          sunday: 'Inchis'
        }
      };

      setClinic(processedClinic);
    } catch (err: any) {
      // Nu logăm eroarea dacă backend-ul nu rulează
      if (!(err?.message?.includes('Failed to fetch') || err?.message?.includes('Network Error') || err?.code === 'ERR_NETWORK')) {
        console.error('Error loading clinic details:', err);
      }
      setError('Eroare la incarcarea detaliilor clinicii');
    } finally {
      setLoading(false);
    }
  };

  const loadVeterinarians = async () => {
    try {
      if (!clinicId) return;
      
      const response = await api.get(`/clinics/${clinicId}/vets`);
      setVeterinarians(response.data.data || []);
    } catch (err: any) {
      // Nu logăm eroarea dacă backend-ul nu rulează
      if (!(err?.message?.includes('Failed to fetch') || err?.message?.includes('Network Error') || err?.code === 'ERR_NETWORK')) {
        console.error('Error loading veterinarians:', err);
      }
    }
  };


  const toggleFavorite = async () => {
    if (!clinic) return;
    
    try {
      await api.post(`/clinics/${clinic.id}/favorite`);
      setClinic(prev => prev ? { ...prev, isFavorite: !prev.isFavorite } : null);
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const handleAppointment = () => {
    navigate(`/client/clinics/${clinicId}/appointments/new`);
  };

  const handleDirections = () => {
    if (clinic?.addressText) {
      const encodedAddress = encodeURIComponent(`${clinic.addressText}, ${clinic.city}`);
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Se incarca detaliile clinicii...
        </Typography>
      </Container>
    );
  }

  if (error || !clinic) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Clinica nu a fost gasita'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/client/clinics')}>
          Inapoi la clinici
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header cu coperta */}
      <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ 
          height: 200, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Favorite Button */}
          <IconButton
            onClick={toggleFavorite}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              bgcolor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
            }}
          >
            {clinic.isFavorite ? (
              <FavoriteIcon color="error" />
            ) : (
              <FavoriteBorderIcon />
            )}
          </IconButton>

          {/* Logo mare */}
          <Avatar
            src={clinic.logo}
            sx={{ 
              width: 120, 
              height: 120, 
              border: '6px solid white',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              bgcolor: 'primary.main'
            }}
          >
            <BusinessIcon sx={{ fontSize: '3rem' }} />
          </Avatar>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              {clinic.name}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Rating value={clinic.rating} precision={0.1} size="large" readOnly />
              <Typography variant="h6" color="text.secondary">
                ⭐ {clinic.rating.toFixed(1)} ({clinic.reviewsCount} recenzii)
              </Typography>
            </Box>

            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              {clinic.description}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<ScheduleIcon />}
              onClick={handleAppointment}
              sx={{ borderRadius: 3, px: 4, py: 1.5, fontSize: '1.1rem' }}
            >
              Programeaza vizita
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<DirectionsIcon />}
              onClick={handleDirections}
              sx={{ borderRadius: 3, px: 4, py: 1.5, fontSize: '1.1rem' }}
            >
              Directii
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Informatii de contact */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon color="primary" />
                Informatii de contact
              </Typography>
              
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <LocationIcon color="action" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Adresa"
                    secondary={`${clinic.addressText || 'Adresa nedisponibila'}, ${clinic.city}`}
                  />
                </ListItem>
                
                {clinic.phone && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <PhoneIcon color="action" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Telefon"
                      secondary={clinic.phone}
                    />
                  </ListItem>
                )}
                
                {clinic.email && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <EmailIcon color="action" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Email"
                      secondary={clinic.email}
                    />
                  </ListItem>
                )}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimeIcon color="primary" />
                Program de lucru
              </Typography>
              
              {clinic.workingHours && (
                <Box sx={{ display: 'grid', gap: 0.5 }}>
                  {Object.entries(clinic.workingHours).map(([day, hours]) => {
                    // Formatare ore lucru
                    let hoursText = 'Nedisponibil';
                    if (typeof hours === 'string') {
                      hoursText = hours;
                    } else if (hours && typeof hours === 'object' && !Array.isArray(hours) && hours !== null) {
                      const hoursObj = hours as { start?: string; end?: string; closed?: boolean };
                      if (hoursObj.closed === true) {
                        hoursText = 'Inchis';
                      } else if (hoursObj.start && hoursObj.end) {
                        hoursText = `${hoursObj.start} - ${hoursObj.end}`;
                      }
                    }
                    
                    // Ensure we always have a string, never render an object
                    const displayText = String(hoursText || 'Nedisponibil');
                    
                    return (
                      <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {day === 'monday' ? 'Luni' :
                           day === 'tuesday' ? 'Marti' :
                           day === 'wednesday' ? 'Miercuri' :
                           day === 'thursday' ? 'Joi' :
                           day === 'friday' ? 'Vineri' :
                           day === 'saturday' ? 'Sambata' :
                           day === 'sunday' ? 'Duminica' : day}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {displayText}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>


        {/* Servicii */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                🏥 Servicii oferite
              </Typography>
              
              {clinic.services && clinic.services.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {clinic.services.map((service, idx) => (
                    <Chip
                      key={idx}
                      label={service}
                      sx={{ 
                        mb: 1,
                        bgcolor: 'primary.light',
                        color: 'white',
                        '& .MuiChip-label': { px: 2 }
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Informatii despre servicii nu sunt disponibile
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>
                🎯 Specializari
              </Typography>
              
              {clinic.specialties && clinic.specialties.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {clinic.specialties.map((specialty, idx) => (
                    <Chip
                      key={idx}
                      label={specialty}
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Informatii despre specializari nu sunt disponibile
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Medicii clinicii */}
        <Grid size={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DoctorIcon color="primary" />
                Medicii clinicii
              </Typography>
              
              {veterinarians.length > 0 ? (
                <Grid container spacing={2}>
                  {veterinarians.map((vet) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={vet.id}>
                      <Card sx={{ borderRadius: 2, textAlign: 'center', p: 2 }}>
                        <Avatar 
                          src={vet.photo_url}
                          sx={{ 
                            width: 80, 
                            height: 80, 
                            mx: 'auto', 
                            mb: 2,
                            bgcolor: 'primary.main',
                            fontSize: '1.8rem'
                          }}
                        >
                          👩‍⚕️
                        </Avatar>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Dr. {vet.firstName} {vet.lastName}
                        </Typography>
                        <Typography variant="body2" color="primary.main" fontWeight={500}>
                          {vet.specialization || 'Medicina generala'}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Informatii despre medici nu sunt disponibile
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Buton inapoi */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/client/clinics')}
          sx={{ borderRadius: 3, px: 4 }}
        >
          ← Inapoi la clinici partenere
        </Button>
      </Box>
    </Container>
  );
};

export default ClinicDetails;
